const EMPTY_FILTER_GROUP = { logic: "and", conditions: [] };

let filterIdCounter = 0;
const createId = () => `filter_${Date.now()}_${filterIdCounter++}`;

export const getEmptyFilterGroup = () => ({ ...EMPTY_FILTER_GROUP });

export const enhanceFilterGroup = (group = EMPTY_FILTER_GROUP) => {
  const safeGroup = group || EMPTY_FILTER_GROUP;
  return {
    logic: (safeGroup.logic || "and").toLowerCase(),
    id: createId(),
    conditions: Array.isArray(safeGroup.conditions)
      ? safeGroup.conditions.map((condition) => {
          if (condition && Array.isArray(condition.conditions)) {
            return enhanceFilterGroup(condition);
          }
          return {
            field: condition?.field || "",
            operator: condition?.operator || "",
            value: condition?.value ?? "",
            values: Array.isArray(condition?.values) ? condition.values : [],
            id: createId(),
          };
        })
      : [],
  };
};

const normalizePrimitiveValue = (rawValue, fieldMeta = {}) => {
  const fieldType = fieldMeta.type || "string";
  if (fieldType === "boolean") {
    if (typeof rawValue === "boolean") {
      return rawValue;
    }
    if (typeof rawValue === "string") {
      const lowered = rawValue.trim().toLowerCase();
      return lowered === "true" || lowered === "1" || lowered === "yes" || lowered === "oui";
    }
    return Boolean(rawValue);
  }
  if (fieldType === "numeric") {
    if (rawValue === "" || rawValue === null || rawValue === undefined) {
      return null;
    }
    const parsed = Number(rawValue);
    return Number.isNaN(parsed) ? null : parsed;
  }
  return rawValue;
};

const sanitizeCondition = (condition, fieldMap) => {
  if (!condition) {
    return null;
  }
  if (Array.isArray(condition.conditions)) {
    const nested = stripFilterMeta(condition, fieldMap);
    if (!isGroupEmpty(nested)) {
      return nested;
    }
    return null;
  }

  if (!condition.field || !condition.operator) {
    return null;
  }

  const operator = condition.operator;
  const sanitized = {
    field: condition.field,
    operator,
  };
  const fieldMeta = fieldMap?.[condition.field] || {};

  if (operator === "between") {
    const values = Array.isArray(condition.values) ? condition.values : [];
    if (values.length === 2 && values[0] && values[1]) {
      sanitized.values = values.map((value) => normalizePrimitiveValue(value, fieldMeta));
      return sanitized;
    }
    return null;
  }

  if (operator === "in" || operator === "notin") {
    const values = Array.isArray(condition.values) ? condition.values.filter(Boolean) : [];
    if (values.length) {
      sanitized.values = values.map((value) => normalizePrimitiveValue(value, fieldMeta)).filter((value) => value !== null && value !== undefined && value !== "");
      return sanitized;
    }
    return null;
  }

  if (operator === "isempty" || operator === "isnotempty") {
    return sanitized;
  }

  if (condition.value === null || condition.value === undefined || condition.value === "") {
    return null;
  }

  const normalizedValue = normalizePrimitiveValue(condition.value, fieldMeta);
  if (normalizedValue === null || normalizedValue === undefined || normalizedValue === "") {
    return null;
  }
  sanitized.value = normalizedValue;
  return sanitized;
};

export const stripFilterMeta = (group, fieldMap = {}) => {
  if (!group) {
    return getEmptyFilterGroup();
  }

  const sanitizedConditions = Array.isArray(group.conditions)
    ? group.conditions
        .map((condition) => sanitizeCondition(condition, fieldMap))
        .filter((condition) => condition !== null)
    : [];

  return {
    logic: (group.logic || "and").toLowerCase(),
    conditions: sanitizedConditions,
  };
};

export const isGroupEmpty = (group) => {
  if (!group || !Array.isArray(group.conditions) || group.conditions.length === 0) {
    return true;
  }
  return group.conditions.every((condition) => {
    if (condition && Array.isArray(condition.conditions)) {
      return isGroupEmpty(condition);
    }
    if (!condition) {
      return true;
    }
    if (!condition.field || !condition.operator) {
      return true;
    }
    if (condition.operator === "between") {
      return !(Array.isArray(condition.values) && condition.values[0] && condition.values[1]);
    }
    if (condition.operator === "in" || condition.operator === "notin") {
      return !(Array.isArray(condition.values) && condition.values.length);
    }
    if (condition.operator === "isempty" || condition.operator === "isnotempty") {
      return false;
    }
    return condition.value === "" || condition.value === null || condition.value === undefined;
  });
};

export const ensureFilterGroup = (group) => stripFilterMeta(group || getEmptyFilterGroup());
